import { useState, useEffect, useRef, useCallback } from 'react'
import type { Job, Message, Operation } from '../types'
import type { GCodeResponse } from '../services/types'
import * as db from '../services/db'
import { loadPersistedTabs, savePersistedTabs, addOpenTab, removeOpenTab } from '../lib/tabStorage'

function updateOp(jobs: Job[], jobId: string, opId: string, patch: Partial<Operation>): Job[] {
  return jobs.map(j => j.id !== jobId ? j : {
    ...j,
    operations: j.operations.map(op => op.id !== opId ? op : { ...op, ...patch }),
  })
}

export function useJobs() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Persist tab state whenever jobs or activeJobId change
  const persistRef = useRef(false)

  useEffect(() => {
    // On mount: restore from localStorage
    const persisted = loadPersistedTabs()
    const ids = persisted.openJobIds

    if (ids.length === 0) {
      setLoading(false)
      return
    }

    db.fetchJobsByIds(ids)
      .then(fetched => {
        setJobs(fetched)
        // Restore active job — fall back to last fetched if saved active is gone
        const restoredActive = fetched.find(j => j.id === persisted.activeJobId)
          ? persisted.activeJobId
          : fetched.length > 0 ? fetched[fetched.length - 1].id : null
        setActiveJobId(restoredActive)
        // Clean up localStorage if some jobs were deleted from DB
        const fetchedIds = fetched.map(j => j.id)
        const cleanedIds = ids.filter(id => fetchedIds.includes(id))
        savePersistedTabs({ activeJobId: restoredActive, openJobIds: cleanedIds })
        persistRef.current = true
      })
      .catch(err => setError(String(err?.message ?? err)))
      .finally(() => setLoading(false))
  }, [])

  // Sync localStorage when jobs or activeJobId change (after initial mount)
  useEffect(() => {
    if (!persistRef.current) return
    savePersistedTabs({
      activeJobId,
      openJobIds: jobs.map(j => j.id),
    })
  }, [jobs, activeJobId])

  const activeJob = jobs.find(j => j.id === activeJobId) ?? null

  const addJob = useCallback(async (job: Job) => {
    setJobs(prev => [...prev, job])
    addOpenTab(job.id)
    persistRef.current = true

    await db.insertJob(job)
    await Promise.all(job.messages.map(msg => db.insertMessage(job.id, msg)))
  }, [])

  const closeJob = useCallback((id: string, onNoActive?: () => void) => {
    // Compute navigation decision BEFORE the state updater to avoid
    // calling navigate() inside setJobs (setState-during-render violation)
    const remaining = jobs.filter(j => j.id !== id)
    const needsNavigate = activeJobId === id && remaining.length === 0

    setJobs(prev => {
      const next = prev.filter(j => j.id !== id)
      if (activeJobId === id) {
        const newActive = next.length > 0 ? next[next.length - 1].id : null
        setActiveJobId(newActive)
      }
      return next
    })
    if (needsNavigate) onNoActive?.()
    removeOpenTab(id)
    db.closeJob(id).catch(err => {
      setError(String(err?.message ?? err))
    })
  }, [activeJobId, jobs])

  const deleteJob = useCallback((id: string) => {
    setJobs(prev => {
      const next = prev.filter(j => j.id !== id)
      if (activeJobId === id) {
        const newActive = next.length > 0 ? next[next.length - 1].id : null
        setActiveJobId(newActive)
      }
      return next
    })
    removeOpenTab(id)
    db.deleteJob(id).catch(err => {
      setError(String(err?.message ?? err))
    })
  }, [activeJobId])

  const reopenJob = useCallback(async (id: string) => {
    // Check if already in local state
    const existing = jobs.find(j => j.id === id)
    if (existing) {
      setActiveJobId(id)
      return
    }
    // Fetch from DB
    const fetched = await db.fetchJobsByIds([id])
    if (fetched.length === 0) return
    setJobs(prev => [...prev, fetched[0]])
    setActiveJobId(id)
    addOpenTab(id)
    db.reopenJob(id).catch(err => setError(String(err?.message ?? err)))
  }, [jobs])

  const updateOperationGCode = useCallback((jobId: string, opId: string, gcode: GCodeResponse) => {
    setJobs(prev => {
      const updated = updateOp(prev, jobId, opId, { isGenerating: false, gcode })
      const job = updated.find(j => j.id === jobId)
      if (job) db.updateJob(jobId, { operations: job.operations }).catch(err => setError(String(err?.message ?? err)))
      return updated
    })
  }, [])

  // Debounced DB persist for streaming updates — save at most every 2s
  const streamingPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateStreamingOperationGCode = useCallback((jobId: string, opId: string, gcode: GCodeResponse) => {
    setJobs(prev => {
      const updated = updateOp(prev, jobId, opId, { gcode })
      // Debounce DB persist so we don't write on every keystroke/patch
      if (streamingPersistTimer.current) clearTimeout(streamingPersistTimer.current)
      streamingPersistTimer.current = setTimeout(() => {
        const job = updated.find(j => j.id === jobId)
        if (job) db.updateJob(jobId, { operations: job.operations }).catch(() => {})
      }, 2000)
      return updated
    })
  }, [])

  const updateJobFields = useCallback((
    id: string,
    updates: Partial<Pick<Job, 'material' | 'units' | 'operations' | 'activeOperationId' | 'stockSize' | 'tools' | 'notes' | 'agentSessionId' | 'memory' | 'extractedGeometry' | 'extractedSummary'>>,
  ) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...updates } : j))
    db.updateJob(id, updates).catch(err => {
      setError(String(err?.message ?? err))
    })
  }, [])

  const setOperationGenerating = useCallback((jobId: string, opId: string, isGenerating: boolean) => {
    setJobs(prev => updateOp(prev, jobId, opId, { isGenerating }))
  }, [])

  const addOperation = useCallback((jobId: string, operation: Operation) => {
    setJobs(prev => {
      const updated = prev.map(j => j.id !== jobId ? j : {
        ...j,
        operations: [...j.operations, operation],
        activeOperationId: operation.id,
      })
      const job = updated.find(j => j.id === jobId)
      if (job) db.updateJob(jobId, { operations: job.operations, activeOperationId: job.activeOperationId }).catch(err => setError(String(err?.message ?? err)))
      return updated
    })
  }, [])

  const removeOperation = useCallback((jobId: string, opId: string) => {
    setJobs(prev => {
      const current = prev.find(j => j.id === jobId)
      if (!current || current.operations.length <= 1) return prev
      const updated = prev.map(j => {
        if (j.id !== jobId) return j
        const ops = j.operations.filter(op => op.id !== opId)
        const activeId = j.activeOperationId === opId
          ? (ops[ops.length - 1]?.id ?? '')
          : j.activeOperationId
        return { ...j, operations: ops, activeOperationId: activeId }
      })
      const updatedJob = updated.find(j => j.id === jobId)
      if (updatedJob) db.updateJob(jobId, { operations: updatedJob.operations, activeOperationId: updatedJob.activeOperationId }).catch(err => setError(String(err?.message ?? err)))
      return updated
    })
  }, [])

  const appendMessage = useCallback((jobId: string, msg: Message) => {
    setJobs(prev =>
      prev.map(j =>
        j.id === jobId ? { ...j, messages: [...j.messages, msg] } : j,
      ),
    )
    db.insertMessage(jobId, msg).catch(err => setError(String(err?.message ?? err)))
    db.touchJob(jobId).catch(() => undefined)
  }, [])

  return {
    jobs,
    setJobs,
    activeJob,
    activeJobId,
    setActiveJobId,
    loading,
    error,
    addJob,
    closeJob,
    deleteJob,
    reopenJob,
    updateOperationGCode,
    updateStreamingOperationGCode,
    updateJobFields,
    setOperationGenerating,
    addOperation,
    removeOperation,
    appendMessage,
  }
}
